import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageContainer from '../components/layout/PageContainer';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { QRCodeSVG } from 'qrcode.react';

interface Coupon {
  id: string;
  title: string;
  image: string;
  category: string;
  expiresAt: string;
  business: string;
  summary: string;
  content: string;
  discount: string;
  validUntil: string;
  terms?: string;
}

// Helper to safely format date
function formatDate(dateString: string) {
  if (!dateString) return 'Data inválida';
  let date = new Date(dateString);
  if (isNaN(date.getTime())) {
    // Try DD/MM/YYYY
    const parts = dateString.split('/');
    if (parts.length === 3) {
      // new Date(year, monthIndex, day)
      date = new Date(+parts[2], +parts[1] - 1, +parts[0]);
    }
  }
  if (isNaN(date.getTime())) return 'Data inválida';
  return date.toLocaleDateString('pt-BR');
}

const CouponsList = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [showQRCode, setShowQRCode] = useState(false);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const response = await fetch('https://us-central1-appdizquetem.cloudfunctions.net/getCouponList');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Received coupons data:', data);
        setCoupons(data);
      } catch (error) {
        console.error('Error loading coupons:', error);
        setError(error instanceof Error ? error.message : 'Failed to load coupons');
      } finally {
        setLoading(false);
      }
    };

    fetchCoupons();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-64px)]">
          <h2 className="text-xl font-semibold text-white mb-4">Error loading coupons</h2>
          <p className="text-white/60">{error}</p>
        </div>
      </PageContainer>
    );
  }

  if (coupons.length === 0) {
    return (
      <PageContainer>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-white">
            Promoções
          </h1>
        </div>
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)]">
          <p className="text-white/60">Nenhuma promoção disponível no momento.</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-white">
          Promoções
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {coupons.map((coupon, index) => (
          <motion.div
            key={coupon.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            <button
              onClick={() => setSelectedCoupon(coupon)}
              className="w-full block bg-white/10 backdrop-blur-lg rounded-xl overflow-hidden hover:bg-white/20 transition-colors h-full relative"
            >
              {/* Coupon border effect */}
              <div className="absolute inset-0 border-2 border-dashed border-white/20 rounded-xl pointer-events-none" />
              
              <div className="flex flex-col h-full">
                <div className="relative">
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={coupon.image}
                      alt={coupon.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Discount and date badge */}
                  <div className="absolute top-2 right-2 bg-primary text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex flex-col items-end">
                    <span>{coupon.discount}</span>
                    <span className="font-normal text-[10px] leading-tight">até {formatDate(coupon.expiresAt)}</span>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-primary text-sm font-medium">
                      {coupon.category}
                    </span>
                  </div>
                  <h2 className="text-white font-medium text-lg mb-2 line-clamp-2">
                    {coupon.title}
                  </h2>
                  <p className="text-white/60 text-sm mb-2 line-clamp-2">
                    {coupon.summary}
                  </p>
                  <div className="mt-auto">
                    <div className="text-white/40 text-sm">
                      {coupon.business}
                    </div>
                  </div>
                </div>
                {/* Coupon cut line */}
                <div className="h-6 relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-dashed border-white/20" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-background px-2 text-white/40 text-xs">
                      Toque para ver detalhes
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </motion.div>
        ))}
      </div>

      {/* Coupon Detail Modal */}
      {selectedCoupon && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-background rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative"
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedCoupon(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-white" />
            </button>

            {/* Coupon content */}
            <div className="relative">
              <div className="aspect-w-16 aspect-h-9">
                <img
                  src={selectedCoupon.image}
                  alt={selectedCoupon.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                {selectedCoupon.discount}
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-primary text-sm font-medium">
                  {selectedCoupon.category}
                </span>
                <span className="text-white/40 text-sm">
                  Válido até {formatDate(selectedCoupon.expiresAt)}
                </span>
              </div>

              <h2 className="text-2xl font-semibold text-white mb-2">
                {selectedCoupon.title}
              </h2>

              <div className="text-white/40 text-sm mb-4">
                {selectedCoupon.business}
              </div>

              <div className="prose prose-invert max-w-none mb-6">
                {selectedCoupon.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-white/80 mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>

              {selectedCoupon.terms && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Termos e Condições
                  </h3>
                  <div className="prose prose-invert max-w-none">
                    {selectedCoupon.terms.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="text-white/60 text-sm mb-2">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 flex gap-4">
                <button
                  onClick={() => setSelectedCoupon(null)}
                  className="flex-1 py-3 px-6 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                >
                  Fechar
                </button>
                <button
                  onClick={() => setShowQRCode(true)}
                  className="flex-1 py-3 px-6 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
                >
                  Resgatar Cupom
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* QR Code Modal - Moved outside as a sibling */}
      {showQRCode && selectedCoupon && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-background rounded-xl w-full max-w-md p-6 relative"
          >
            {/* Close button */}
            <button
              onClick={() => setShowQRCode(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-white" />
            </button>

            <div className="text-center">
              <h2 className="text-xl font-semibold text-white mb-4">
                Seu Cupom
              </h2>
              
              <div className="bg-white p-4 rounded-lg inline-block mb-4">
                <QRCodeSVG
                  value={JSON.stringify({
                    id: selectedCoupon.id,
                    title: selectedCoupon.title,
                    discount: selectedCoupon.discount,
                    business: selectedCoupon.business,
                    expiresAt: selectedCoupon.expiresAt
                  })}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>

              <p className="text-white/60 text-sm mb-6">
                Mostre este QR Code ao atendente para resgatar seu cupom
              </p>

              <div className="flex flex-col gap-2 text-left bg-white/10 rounded-lg p-4 mb-6">
                <div>
                  <span className="text-white/40 text-sm">Cupom:</span>
                  <p className="text-white font-medium">{selectedCoupon.title}</p>
                </div>
                <div>
                  <span className="text-white/40 text-sm">Desconto:</span>
                  <p className="text-white font-medium">{selectedCoupon.discount}</p>
                </div>
                <div>
                  <span className="text-white/40 text-sm">Estabelecimento:</span>
                  <p className="text-white font-medium">{selectedCoupon.business}</p>
                </div>
                <div>
                  <span className="text-white/40 text-sm">Válido até:</span>
                  <p className="text-white font-medium">{formatDate(selectedCoupon.expiresAt)}</p>
                </div>
              </div>

              <button
                onClick={() => setShowQRCode(false)}
                className="w-full py-3 px-6 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                Fechar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </PageContainer>
  );
};

export default CouponsList; 